import moment from 'moment'
import store from '../store'

/**
 * 格式化日期
 * @param {string|Date} date - 要格式化的日期
 * @param {string} [fmt='YYYY-MM-DD'] - 格式化字符串
 * @returns {string} - 格式化后的日期字符串
 */
export const date = (date, fmt = 'YYYY-MM-DD') => {
  let str = date || ''
  if (typeof str === 'string' && str && (str.includes('**') || str.includes('✽✽'))) {
    return str
  }
  return str ? moment(str).format(fmt) : str
}

/**
 * 格式化时间
 * @param {string|Date} d - 要格式化的时间
 * @param {string} [fmt='YYYY-MM-DD HH:mm:ss'] - 格式化字符串
 * @returns {string} - 格式化后的时间字符串
 */
export const time = (d, fmt = 'YYYY-MM-DD HH:mm:ss') => {
  return date(d, fmt)
}

/**
 * 格式化分钟
 * @param {string|Date} d - 要格式化的时间
 * @param {string} [fmt='YYYY-MM-DD HH:mm'] - 格式化字符串
 * @returns {string} - 格式化后的分钟字符串
 */
export const minute = (d, fmt = 'YYYY-MM-DD HH:mm') => {
  return date(d, fmt)
}

/**
 * 格式化秒
 * @param {string|Date} d - 要格式化的时间
 * @param {string} [fmt='HH:mm:ss'] - 格式化字符串
 * @returns {string} - 格式化后的秒字符串
 */
export const second = (d, fmt = 'HH:mm:ss') => {
  return date(d, fmt)
}

/**
 * 格式化月份
 * @param {string|Date} d - 要格式化的时间
 * @param {string} [fmt='YYYY-MM'] - 格式化字符串
 * @returns {string} - 格式化后的月份字符串
 */
export const month = (d, fmt = 'YYYY-MM') => {
  return date(d, fmt)
}

/**
 * 格式化金额
 * @param {number|string} num - 要格式化的数字
 * @param {string} [prev='￥'] - 货币符号
 * @param {number} [fixed=2] - 小数位数
 * @returns {string} - 格式化后的金额字符串
 */
export const money = (num, prev = '￥', fixed = 2) => {
  if (typeof num !== 'number' && !num) {
    return ''
  }
  if (typeof num === 'string' && (num.includes('**') || num.includes('✽✽'))) {
    return num
  }
  prev = prev === null ? '￥' : prev
  return `${prev}${(+num).toFixed(fixed).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
}

/**
 * 格式化小数
 * @param {number|string} num - 要格式化的数字
 * @param {number} [precision=2] - 小数位数
 * @returns {string} - 格式化后的小数字符串
 */
export const decimal = (num, precision = 2) => {
  if (typeof num !== 'number' && !num) {
    return ''
  }
  if (typeof num === 'string' && (num.includes('**') || num.includes('✽✽'))) {
    return num
  }
  return `${(+num).toFixed(precision).replace(/\d{1,3}(?=(\d{3})+(\.\d*)?$)/g, '$&,')}`
}

/**
 * 格式化千位数
 * @param {number|string} num - 要格式化的数字
 * @returns {string} - 格式化后的千位数字符串
 */
export const thousands = (num) => {
  if (typeof num !== 'number' && !num) {
    return ''
  }
  if (typeof num === 'string' && (num.includes('**') || num.includes('✽✽'))) {
    return num
  }
  return (+num).toLocaleString()
}


/**
 * 查找数据字典对应的 label
 * @param {string} val - 要查找的字典值
 * @param {string} key - 数据字典的 key
 * @returns {string} - 找到的标签或原值
 *//* eslint-disable eqeqeq */
export const lookup = (val, key) => {
  let obj = (store.getters.lookUpOptions[key] || []).find(v => v.value == val)
  return obj ? obj.label : val
}

/**
 * 自定义格式化后端时间
 * @param {string|Date} d - 要格式化的时间
 * @param {string} [fmt='YYYY-MM-DD'] - 格式化字符串
 * @returns {string} - 格式化后的时间字符串
 */
export const customDateTime = (d, fmt = 'YYYY-MM-DD') => {
  return date(d, fmt)
}

/**
 * 9级组织过滤器
 * @param val   跨越集团/快递事业部/京津冀大区/取派中心/北京分区/顺义区/顺义取派部/东城片区/顺义八里庄点部@顺义取派部@顺义八里庄点部
 * @param dept  Array：9级组织 | 1：部门 | 2：点部
 * @param split 组织、部门、点部 的分隔符，默认为“@”
 * @returns {string} 根据用户保存的点部配置，解析出对应的部门数据
 */
export const deptFilter = (val, dept, split = '@') => {
  let index = val && val.indexOf(split)
  if (!index || index === -1) {
    return val
  }
  if (Array.isArray(dept)) {
    let arr = val.substring(0, index).split('/')
    let len = Math.min(arr.length, dept.length)
    let str = ''
    for (let i = 0; i < len; i++) {
      let s = arr[dept[i] - 1]
      if (s) {
        str = `${str}/${s}`
      }
    }
    return str ? str.replace(/^\//, '') : val
  } else if (dept === 1) {
    return val.substring(index + 1, val.lastIndexOf(split))
  } else if (dept === 2) {
    return val.substring(val.lastIndexOf(split) + 1)
  } else {
    return val
  }
}

/**
 * 数字切割格式化 234333.22 => 23万 4333 234.22 => 234.22 支持金额切换
 * @param {number|string} val - 要格式化的数字
 * @returns {string} - 格式化后的数字字符串
 */
export const numberSplit = val => {
  // 防止空字符返回为0
  if (val === '') return val
  if (store.getters.globalMoneyFormatType === 'CNY') {
    return money(val)
  }
  let toNumberVal = Number(val)
  if (isNaN(toNumberVal)) {
    return val
  }
  let flag = ''
  if (toNumberVal < 0) {
    flag = '-'
    toNumberVal = Math.abs(toNumberVal)
  }
  if (toNumberVal >= 10000) {
    // 大于等于10000的数据要去掉小数位
    const numStr = Math.floor(toNumberVal) + ''
    const len = numStr.length
    const numArr = []
    // 每4位截取一次，截取小于10000部分的数据
    let itemLast = Number(numStr.substring(len - 4, len))
    // 只有个位数前面要加0 等于0则去掉，不加入数组拼接
    if (itemLast < 10 && itemLast > 0) {
      numArr.unshift('0' + itemLast)
    } else if (itemLast >= 10) {
      numArr.unshift(itemLast)
    }
    let itemCenter = 0
    let itemFirst = 0
    if (len <= 8) {
      // 长度小于等于8， 没有亿位，这个时候万位可以从index = 0 开始截取
      itemCenter = Number(numStr.substring(0, len - 4))
    } else {
      // 长度大于8才有亿， 万位不能再从index = 0开始截取
      itemCenter = Number(numStr.substring(len - 8, len - 4))
      itemFirst = Number(numStr.substring(0, len - 8))
    }
    // 万位和亿位如果大于0才加入数组拼接
    if (itemCenter > 0) numArr.unshift(itemCenter + '万')
    if (itemFirst > 0) numArr.unshift(itemFirst + '亿')
    return flag + numArr.join('')
  }
  return flag + toNumberVal
}

/**
 * 整数数字切割格式化 234333.22 => 23万4333 234.22 => 234.22
 * @param {number|string} val - 要格式化的数字
 * @returns {string} - 格式化后的整数数字字符串
 */
export const numberSplitInt = val => {
  // 防止空字符返回为0
  if (val === '') return val
  let toNumberVal = Number(val)
  if (isNaN(toNumberVal)) {
    return val
  }
  let flag = ''
  if (toNumberVal < 0) {
    flag = '-'
    toNumberVal = Math.abs(toNumberVal)
  }
  if (toNumberVal >= 10000) {
    // 大于等于10000的数据要去掉小数位
    const numStr = Math.floor(toNumberVal) + ''
    const len = numStr.length
    const numArr = []
    // 每4位截取一次，截取小于10000部分的数据
    let itemLast = Number(numStr.substring(len - 4, len))
    // 只有个位数前面要加0 等于0则去掉，不加入数组拼接
    if (itemLast < 10 && itemLast > 0) {
      numArr.unshift('0' + itemLast)
    } else if (itemLast >= 10) {
      numArr.unshift(itemLast)
    }
    let itemCenter = 0
    let itemFirst = 0
    if (len <= 8) {
      // 长度小于等于8， 没有亿位，这个时候万位可以从index = 0 开始截取
      itemCenter = Number(numStr.substring(0, len - 4))
    } else {
      // 长度大于8才有亿， 万位不能再从index = 0开始截取
      itemCenter = Number(numStr.substring(len - 8, len - 4))
      itemFirst = Number(numStr.substring(0, len - 8))
    }
    // 万位和亿位如果大于0才加入数组拼接
    if (itemCenter > 0) numArr.unshift(itemCenter + '万')
    if (itemFirst > 0) numArr.unshift(itemFirst + '亿')
    return flag + numArr.join('')
  }
  return flag + toNumberVal
}

/**
 * 浮点数数字切割格式化 234333.22 => 23万4333.22 234.22 => 234.22
 * @param {number|string} val - 要格式化的数字
 * @param {number} decimal - 小数位数
 * @param {boolean} globalMoneyFormat - 是否使用全局货币格式
 * @returns {string} - 格式化后的浮点数数字字符串
 */
export const numberSplitFloat = (val, decimal, globalMoneyFormat = false) => {
  // 防止空字符返回为0
  if (val === '') return val
  if (globalMoneyFormat && store.getters.globalMoneyFormatType === 'CNY') {
    return money(val)
  }
  if (decimal < 0 || !Number.isInteger(decimal)) decimal = void 0
  let toNumberVal = Number(val)
  if (isNaN(toNumberVal)) {
    return val
  }
  let flag = ''
  if (toNumberVal < 0) {
    flag = '-'
    toNumberVal = Math.abs(toNumberVal)
  }
  const numStrArr = String(toNumberVal).split('.')
  const numStr = numStrArr[0]
  const _decimal = decimal == 0 ? '' : (decimal || (numStrArr[1] && numStrArr[1].length) || '')
  const decimalNum = numStrArr[1] && _decimal ? `.${numStrArr[1].substring(0, _decimal)}` : ''
  if (toNumberVal >= 10000) {
    const len = numStr.length
    const numArr = []
    // 每4位截取一次，截取小于10000部分的数据
    let itemLast = Number(numStr.substring(len - 4, len))
    // 只有个位数前面要加0 等于0则去掉，不加入数组拼接
    if (itemLast < 10 && itemLast > 0) {
      numArr.unshift('0' + itemLast)
    } else if (itemLast >= 10) {
      numArr.unshift(itemLast)
    }
    let itemCenter = 0
    let itemFirst = 0
    if (len <= 8) {
      // 长度小于等于8， 没有亿位，这个时候万位可以从index = 0 开始截取
      itemCenter = Number(numStr.substring(0, len - 4))
    } else {
      // 长度大于8才有亿， 万位不能再从index = 0开始截取
      itemCenter = Number(numStr.substring(len - 8, len - 4))
      itemFirst = Number(numStr.substring(0, len - 8))
    }
    // 万位和亿位如果大于0才加入数组拼接
    if (itemCenter > 0) numArr.unshift(itemCenter + '万')
    if (itemFirst > 0) numArr.unshift(itemFirst + '亿')
    return flag + numArr.join('') + decimalNum
  }
  return flag + Math.floor(toNumberVal) + decimalNum
}




// WEBPACK FOOTER //
// ./src/public/utils/filter.js